// Nested App Authentication (NAA) — Microsoft's recommended modern auth for
// Office add-ins. Uses MSAL Browser's createNestablePublicClientApplication,
// which is brokered by the Office host (no real popup is shown in the task
// pane iframe — Office handles the interactive prompt outside the iframe).
//
// We tried legacy Office SSO (OfficeRuntime.auth.getAccessToken) first and
// got error 13006 ("Client Error" in Office on the web). Microsoft now
// explicitly recommends NAA over the legacy SSO path.
import {
  createNestablePublicClientApplication,
  type IPublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { addBreadcrumb } from '../breadcrumbs/breadcrumbs';

let cachedClient: IPublicClientApplication | null = null;

async function getClient(): Promise<IPublicClientApplication> {
  if (cachedClient) return cachedClient;
  cachedClient = await createNestablePublicClientApplication({
    auth: {
      clientId: import.meta.env.VITE_AAD_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AAD_TENANT_ID}`,
      // brk-multihub://<domain> + the page URL are both registered as SPA
      // redirects in the Entra app; MSAL picks the right one automatically.
      redirectUri: `${import.meta.env.VITE_FRONT_BASE_URL}/blank.html`,
    },
    cache: {
      cacheLocation: 'localStorage',
    },
  });
  return cachedClient;
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms} ms`)), ms),
    ),
  ]);
}

// Serialize token acquisition. On first load several queries (containers,
// whoami, …) call this concurrently; each falling back to an interactive
// flow makes MSAL throw `interaction_in_progress` for all but one. Sharing a
// single in-flight promise means concurrent callers await the same result.
let inFlight: Promise<string> | null = null;

export function acquireAccessToken(): Promise<string> {
  if (inFlight) return inFlight;
  inFlight = doAcquire().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doAcquire(): Promise<string> {
  const client = await withTimeout(getClient(), 10_000, 'createNestablePublicClientApplication');
  const request = { scopes: [import.meta.env.VITE_API_SCOPE] };
  try {
    addBreadcrumb({ category: 'auth', message: 'NAA acquireTokenSilent' });
    const result: AuthenticationResult = await withTimeout(
      client.acquireTokenSilent(request),
      15_000,
      'acquireTokenSilent',
    );
    return result.accessToken;
  } catch (errSilent) {
    // The Office host broker can emit error categories that aren't strictly
    // InteractionRequiredAuthError (e.g. WAM IncorrectConfiguration 90009).
    // Fall back to the brokered interactive flow on any silent failure -
    // worst case it raises a fresh, more specific error.
    addBreadcrumb({
      category: 'auth',
      message: 'NAA silent failed, trying brokered popup',
      data: {
        name: (errSilent as Error)?.name,
        message: (errSilent as Error)?.message,
      },
    });
    try {
      const result: AuthenticationResult = await withTimeout(
        client.acquireTokenPopup(request),
        20_000,
        'acquireTokenPopup',
      );
      return result.accessToken;
    } catch (errPopup) {
      addBreadcrumb({
        category: 'auth',
        message: 'NAA popup failed too',
        data: {
          silent: (errSilent as Error)?.message,
          popup: (errPopup as Error)?.message,
        },
      });
      throw errPopup;
    }
  }
}

// Best-effort Microsoft Graph token for reading the signed-in user's own
// profile (name + photo). SILENT ONLY: we never fall back to an interactive
// prompt for cosmetic profile enrichment — a surprise popup for an avatar would
// be terrible UX. Returns null on any failure; callers degrade gracefully
// (initials + Office display name). User.Read is a default delegated scope, so
// this normally resolves from cache without extra consent.
export async function acquireGraphTokenSilent(): Promise<string | null> {
  try {
    const client = await withTimeout(
      getClient(),
      10_000,
      'createNestablePublicClientApplication',
    );
    const result: AuthenticationResult = await withTimeout(
      client.acquireTokenSilent({ scopes: ['User.Read'] }),
      15_000,
      'acquireGraphTokenSilent',
    );
    return result.accessToken;
  } catch (err) {
    addBreadcrumb({
      category: 'auth',
      message: 'graph token silent failed (profile enrichment skipped)',
      data: { message: (err as Error)?.message },
    });
    return null;
  }
}

/**
 * Scopes Graph nécessaires à « Envoyer et ranger » et à la suppression après
 * rangement. Accordés en DÉLÉGUÉ sur l'app Entra : l'add-in agit au nom de
 * l'utilisateur signé, et n'ouvre donc que sa propre boîte. (En Application, la
 * même app lirait toutes les boîtes du tenant.)
 *
 * `Mail.ReadWrite` couvre la lecture, donc `Mail.Read` serait redondant. Et on
 * n'utilise PAS `translateExchangeIds` — qui aurait exigé un `User.ReadBasic.All`
 * (lecture du profil de tous les utilisateurs) : une propriété étendue posée sur
 * le brouillon suffit à retrouver la copie envoyée.
 */
export const MAIL_SCOPES = [
  'https://graph.microsoft.com/Mail.Send',
  'https://graph.microsoft.com/Mail.ReadWrite',
];

/**
 * Jeton Graph pour agir sur les mails. Contrairement à
 * `acquireGraphTokenSilent` — volontairement silencieux, car il ne sert qu'à
 * enrichir un avatar — celui-ci PEUT ouvrir une invite : l'utilisateur vient de
 * cliquer « Enregistrer et envoyer », un consentement à ce moment-là est attendu
 * et compréhensible, alors qu'échouer en silence laisserait son mail ni envoyé
 * ni rangé sans explication.
 *
 * Lève en cas d'échec : l'appelant doit renoncer à l'envoi, pas le tenter à
 * moitié.
 */
export async function acquireMailToken(): Promise<string> {
  const client = await withTimeout(getClient(), 10_000, 'createNestablePublicClientApplication');
  const request = { scopes: MAIL_SCOPES };
  try {
    addBreadcrumb({ category: 'auth', message: 'mail scopes acquireTokenSilent' });
    const result = await withTimeout(client.acquireTokenSilent(request), 15_000, 'mail silent');
    return result.accessToken;
  } catch (errSilent) {
    addBreadcrumb({
      category: 'auth',
      message: 'mail scopes silent failed, trying brokered popup',
      data: { message: (errSilent as Error)?.message },
    });
    const result = await withTimeout(client.acquireTokenPopup(request), 20_000, 'mail popup');
    return result.accessToken;
  }
}

export function getUserOid(claims: Record<string, unknown> | null): string | null {
  if (!claims) return null;
  const oid = claims['oid'];
  return typeof oid === 'string' ? oid : null;
}
